// Public fee comparison page — sortable side-by-side view of all universities.
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpDown, ArrowUp, ArrowDown, Search, ExternalLink, Coins, Trophy, Building2, X, FileDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import PageHero, { CTABlock } from '../components/PageHero';
import { universities as fallbackUnis } from '../data/mock';
import { useUniversities } from '../hooks/useUniversities';

const BASE = process.env.REACT_APP_BACKEND_URL;
const resolveImg = (src) => (src && src.startsWith('/') ? `${BASE}${src}` : src);

const fmtRub = (n) => (n ? `₽${Number(n).toLocaleString('en-IN')}` : '—');

// Derive best-effort tuition-per-year number for sorting
const yearlyRub = (u) => u.annualFeeRUB || (u.annualFeeUSD ? Math.round(u.annualFeeUSD * 90) : 0);
const totalRub = (u) => u.totalFeeRUB || yearlyRub(u) * 6;
const totalInr = (u) => Math.round(totalRub(u) * 1.23);

function SortIcon({ colKey, sortState }) {
  if (sortState.key !== colKey) return <ArrowUpDown className="w-3.5 h-3.5 opacity-60" />;
  return sortState.dir === 'asc' ? <ArrowUp className="w-3.5 h-3.5" /> : <ArrowDown className="w-3.5 h-3.5" />;
}

export default function FeeComparison() {
  const list = useUniversities(fallbackUnis);
  const universities = list.length ? list : fallbackUnis;

  const [query, setQuery] = useState('');
  const [sort, setSort] = useState({ key: 'yearly', dir: 'asc' });
  const [region, setRegion] = useState('all');

  const regions = useMemo(() => {
    const set = new Set(universities.map((u) => u.region).filter(Boolean));
    return ['all', ...Array.from(set).sort()];
  }, [universities]);

  const rows = useMemo(() => {
    let arr = universities.filter((u) => {
      const q = query.trim().toLowerCase();
      const match = !q || (u.name || '').toLowerCase().includes(q) || (u.city || '').toLowerCase().includes(q);
      const regionMatch = region === 'all' || u.region === region;
      return match && regionMatch;
    });
    const getKey = {
      name: (u) => (u.name || '').toLowerCase(),
      city: (u) => (u.city || '').toLowerCase(),
      yearly: (u) => yearlyRub(u),
      total: (u) => totalRub(u),
      inr: (u) => totalInr(u),
      founded: (u) => u.founded || 0,
    }[sort.key] || ((u) => yearlyRub(u));
    arr.sort((a, b) => {
      const av = getKey(a), bv = getKey(b);
      if (av < bv) return sort.dir === 'asc' ? -1 : 1;
      if (av > bv) return sort.dir === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [universities, query, sort, region]);

  const toggleSort = (key) => setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));

  const cheapest = useMemo(() => {
    const with_fee = universities.filter((u) => yearlyRub(u) > 0);
    if (!with_fee.length) return null;
    return with_fee.reduce((a, b) => (yearlyRub(a) < yearlyRub(b) ? a : b));
  }, [universities]);

  const priciest = useMemo(() => {
    const with_fee = universities.filter((u) => yearlyRub(u) > 0);
    if (!with_fee.length) return null;
    return with_fee.reduce((a, b) => (yearlyRub(a) > yearlyRub(b) ? a : b));
  }, [universities]);

  return (
    <div>
      <SEO
        title="MBBS Fees Comparison — All Russian Medical Universities"
        description="Side-by-side comparison of tuition, hostel and 6-year total MBBS fees across every Russian medical university. Sort by cheapest, most expensive or by city."
      />
      <PageHero
        eyebrow="Fees Comparison"
        title="MBBS in Russia — Fees Side-by-Side"
        subtitle={`Compare yearly tuition and 6-year total cost across all ${universities.length} partner universities. Ruble figures per official 2025–26 sheet · 1 ₽ ≈ ₹1.23 INR.`}
      />

      {/* Highlight strip */}
      <section className="py-10 bg-white border-b border-border">
        <div className="container mx-auto max-w-7xl px-4 md:px-8 grid gap-4 sm:grid-cols-3">
          <div className="rounded-2xl border border-border p-5 bg-emerald-50 flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl bg-emerald-500 text-white flex items-center justify-center shrink-0">
              <Coins className="w-5 h-5" />
            </div>
            <div>
              <div className="text-xs font-bold text-emerald-700 uppercase tracking-wider">Most Affordable</div>
              {cheapest ? (
                <>
                  <Link to={`/universities/${cheapest.slug}`} className="font-serif text-xl font-bold text-primary hover:text-brand">
                    {cheapest.name}
                  </Link>
                  <div className="text-xs text-muted-foreground">{fmtRub(yearlyRub(cheapest))}/yr · ≈ ₹{(totalInr(cheapest)/100000).toFixed(2)}L for 6 yrs</div>
                </>
              ) : <div className="text-sm text-muted-foreground">—</div>}
            </div>
          </div>

          <div className="rounded-2xl border border-border p-5 bg-brand/5 flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl bg-brand text-white flex items-center justify-center shrink-0">
              <Trophy className="w-5 h-5" />
            </div>
            <div>
              <div className="text-xs font-bold text-brand uppercase tracking-wider">Premium (Top-ranked)</div>
              {priciest ? (
                <>
                  <Link to={`/universities/${priciest.slug}`} className="font-serif text-xl font-bold text-primary hover:text-brand">
                    {priciest.name}
                  </Link>
                  <div className="text-xs text-muted-foreground">{fmtRub(yearlyRub(priciest))}/yr · ≈ ₹{(totalInr(priciest)/100000).toFixed(2)}L for 6 yrs</div>
                </>
              ) : <div className="text-sm text-muted-foreground">—</div>}
            </div>
          </div>

          <div className="rounded-2xl border border-border p-5 bg-primary/5 flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl bg-primary text-white flex items-center justify-center shrink-0">
              <Building2 className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <div className="text-xs font-bold text-primary uppercase tracking-wider">Choice Range</div>
              <div className="font-serif text-xl font-bold text-primary">{universities.length} Universities</div>
              <div className="text-xs text-muted-foreground">All NMC / WHO approved · English medium</div>
              <a
                href={`${BASE}/api/schedules.zip`}
                target="_blank" rel="noreferrer"
                data-testid="download-all-zip"
                className="mt-3 inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-primary hover:bg-brand text-white text-xs font-bold transition-colors"
              >
                <FileDown className="w-3.5 h-3.5" /> Download All Fees (ZIP)
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 bg-muted/30 border-b border-border sticky top-16 z-30 backdrop-blur">
        <div className="container mx-auto max-w-7xl px-4 md:px-8 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by university name or city…"
              data-testid="fees-search"
              className="w-full h-11 pl-10 pr-10 rounded-lg border border-border bg-white text-sm focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20"
            />
            {query && (
              <button onClick={() => setQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary"><X className="w-4 h-4" /></button>
            )}
          </div>
          <select
            value={region} onChange={(e) => setRegion(e.target.value)}
            data-testid="fees-region-filter"
            className="h-11 px-3 rounded-lg border border-border bg-white text-sm focus:outline-none focus:border-brand w-full sm:w-64"
          >
            {regions.map((r) => <option key={r} value={r}>{r === 'all' ? 'All regions' : r}</option>)}
          </select>
        </div>
      </section>

      {/* Table */}
      <section className="py-10 bg-white">
        <div className="container mx-auto max-w-7xl px-4 md:px-8">
          <div className="overflow-x-auto rounded-2xl border border-border shadow-sm">
            <table className="w-full text-sm min-w-[880px]" data-testid="fees-table">
              <thead className="bg-primary text-white sticky top-0">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold w-12">#</th>
                  <th className="text-left px-4 py-3 font-semibold">
                    <button onClick={() => toggleSort('name')} className="inline-flex items-center gap-1 hover:opacity-80">University <SortIcon colKey="name" sortState={sort} /></button>
                  </th>
                  <th className="text-left px-4 py-3 font-semibold hidden md:table-cell">
                    <button onClick={() => toggleSort('city')} className="inline-flex items-center gap-1 hover:opacity-80">City <SortIcon colKey="city" sortState={sort} /></button>
                  </th>
                  <th className="text-right px-4 py-3 font-semibold">
                    <button onClick={() => toggleSort('yearly')} className="inline-flex items-center gap-1 hover:opacity-80">Yearly Tuition <SortIcon colKey="yearly" sortState={sort} /></button>
                  </th>
                  <th className="text-right px-4 py-3 font-semibold hidden sm:table-cell">
                    <button onClick={() => toggleSort('total')} className="inline-flex items-center gap-1 hover:opacity-80">6-Yr Total (₽) <SortIcon colKey="total" sortState={sort} /></button>
                  </th>
                  <th className="text-right px-4 py-3 font-semibold">
                    <button onClick={() => toggleSort('inr')} className="inline-flex items-center gap-1 hover:opacity-80">6-Yr Total (INR) <SortIcon colKey="inr" sortState={sort} /></button>
                  </th>
                  <th className="text-right px-4 py-3 font-semibold hidden lg:table-cell">
                    <button onClick={() => toggleSort('founded')} className="inline-flex items-center gap-1 hover:opacity-80">Since <SortIcon colKey="founded" sortState={sort} /></button>
                  </th>
                  <th className="text-right px-4 py-3 font-semibold w-24">Details</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((u, i) => (
                  <tr key={u.slug} className={i % 2 === 0 ? 'bg-white hover:bg-brand/5' : 'bg-muted/40 hover:bg-brand/5'}>
                    <td className="px-4 py-3 text-muted-foreground text-xs">{i + 1}</td>
                    <td className="px-4 py-3 min-w-[220px]">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-md overflow-hidden bg-muted border border-border shrink-0">
                          {u.building ? <img src={resolveImg(u.building)} alt="" className="w-full h-full object-cover" /> : null}
                        </div>
                        <div className="min-w-0">
                          <Link to={`/universities/${u.slug}`} className="font-bold text-primary hover:text-brand line-clamp-1">{u.name}</Link>
                          <div className="text-[11px] text-muted-foreground line-clamp-1">{u.type || u.ranking}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell text-primary">{u.city}</td>
                    <td className="px-4 py-3 text-right font-bold text-primary">{fmtRub(yearlyRub(u))}<span className="text-muted-foreground font-normal">/yr</span></td>
                    <td className="px-4 py-3 text-right hidden sm:table-cell">{fmtRub(totalRub(u))}</td>
                    <td className="px-4 py-3 text-right font-bold text-brand">≈ ₹{(totalInr(u)/100000).toFixed(2)}L</td>
                    <td className="px-4 py-3 text-right hidden lg:table-cell text-muted-foreground">{u.founded || '—'}</td>
                    <td className="px-4 py-3 text-right">
                      <Link to={`/universities/${u.slug}`} className="inline-flex items-center gap-1 text-brand text-xs font-bold hover:underline">
                        View <ExternalLink className="w-3 h-3" />
                      </Link>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-10 text-center text-sm text-muted-foreground">
                      No universities match your filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Ruble tuition figures per official 2025–26 fees sheet</span>
            <span>·</span>
            <span>INR estimate at 1 ₽ ≈ ₹1.23 (rate at payment time may vary)</span>
            <span>·</span>
            <span>Excludes food (~$100–120/mo) &amp; medical+visa (~₽6,000/yr)</span>
          </div>
        </div>
      </section>

      <CTABlock
        title="Not sure which one fits your NEET score & budget?"
        subtitle="Our counsellors will shortlist 3 universities matched to your profile — in one 15-minute call."
        cta="Get free shortlist"
      />
    </div>
  );
}
